import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import ReactMarkdown from 'react-markdown';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  BookOpen,
  Upload,
  FileText,
  Download,
  Trash2,
  Sparkles,
  Quote,
  Loader,
  Search,
  Calendar,
  User,
  MessageSquare,
  Send,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  FolderOpen,
  Users,
  Home,
  X,
  Edit2,
  Check,
  Share2,
  Globe,
  Lock
} from 'lucide-react';
import StorageIndicatorHorizontal from '../components/StorageIndicatorHorizontal';
import AICreditsIndicator from '../components/AICreditsIndicator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PREDEFINED_QUESTIONS = [
  "Generate summary of this paper",
  "Results of the paper",
  "Conclusions from the paper",
  "Explain Abstract of this paper",
  "What are the contributions of this paper",
  "Find Related Papers",
  "Explain the practical implications of this paper",
  "Summarise introduction of this paper",
  "Literature survey of this paper",
  "Methods used in this paper",
  "What data has been used in this paper",
  "Limitations of this paper",
  "Future works suggested in this paper"
];

const ResearcherLibraryNew = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  
  // Get translated predefined questions
  const getPredefinedQuestions = () => [
    t('generateSummary'),
    t('resultsOfPaper'),
    t('conclusionsFromPaper'),
    t('explainAbstract'),
    t('contributionsOfPaper'),
    t('findRelatedPapers'),
    t('explainPracticalImplications'),
    t('summariseIntroduction'),
    t('literatureSurvey'),
    t('methodsUsed'),
    t('dataUsedInPaper'),
    t('limitationsOfPaper'),
    t('futureWorks')
  ];
  
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myProfile, setMyProfile] = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentView, setCurrentView] = useState('tldr'); // tldr, content, chat, citation
  const [activeTab, setActiveTab] = useState('myDocuments'); // myDocuments, sharedWithMe
  const [sharedDocuments, setSharedDocuments] = useState([]);
  
  // Rename states
  const [renamingDocId, setRenamingDocId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  
  // Chat states
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const chatEndRef = useRef(null);
  
  // Citation states
  const [citationStyle, setCitationStyle] = useState('APA');
  const [generatedCitation, setGeneratedCitation] = useState('');
  const [citationLoading, setCitationLoading] = useState(false);
  
  // TL;DR state
  const [summarizing, setSummarizing] = useState(false);

  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Related Papers state
  const [relatedPapers, setRelatedPapers] = useState([]);
  const [relatedPapersLoading, setRelatedPapersLoading] = useState(false);
  const [relatedPapersDialogOpen, setRelatedPapersDialogOpen] = useState(false);
  
  // Quick questions select state
  const [selectKey, setSelectKey] = useState(0);
  
  // Share dialog states
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharingStatus, setSharingStatus] = useState('private');
  const [sharingLoading, setSharingLoading] = useState(false);
  const [selectedResearchers, setSelectedResearchers] = useState([]);
  const [allResearchers, setAllResearchers] = useState([]);
  
  // Folder states
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [parentFolderId, setParentFolderId] = useState(null);
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [renameFolderValue, setRenameFolderValue] = useState('');
  const [movingDocId, setMovingDocId] = useState(null);
  const [moveToFolderId, setMoveToFolderId] = useState(null);
  const [shareFolderDialogOpen, setShareFolderDialogOpen] = useState(false);
  const [sharingFolderId, setSharingFolderId] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  
  // AI Credits state
  const [creditsRefreshTrigger, setCreditsRefreshTrigger] = useState(0);

  useEffect(() => {
    if (user) {
      fetchResearcherProfile();
    } else {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (myProfile) {
      fetchFolders();
      fetchDocuments();
      fetchSharedDocuments();
    }
  }, [myProfile]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    setChatMessages([]);
    setChatSessionId(null);
    setChatInput('');
    setGeneratedCitation('');
    setCurrentView('tldr');
  }, [selectedDoc]);

  const fetchResearcherProfile = async () => {
    try {
      setLoading(true);
      setProfileError(null);
      
      // Try to fetch by user_id first
      let response = await fetch(`${BACKEND_URL}/api/researchers/user/${user.id}`);
      
      // If 404, try fetching all profiles and find by email (fallback)
      if (response.status === 404 && user.email) {
        console.log('User ID not found, trying to fetch by email...');
        const allProfilesResponse = await fetch(`${BACKEND_URL}/api/researchers/`);
        if (allProfilesResponse.ok) {
          const allProfiles = await allProfilesResponse.json();
          const profileByEmail = allProfiles.find(p => p.email === user.email);
          if (profileByEmail) {
            setMyProfile(profileByEmail);
            setProfileError(null);
            return;
          }
        }
      }
      
      if (response.ok) {
        const profile = await response.json();
        setMyProfile(profile);
        setProfileError(null);
      } else if (response.status === 404) {
        // Profile doesn't exist
        setProfileError('profile_not_found');
        setMyProfile(null);
      } else {
        console.error('Error fetching profile:', response.status);
        setProfileError('fetch_error');
        setMyProfile(null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfileError('network_error');
      setMyProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (!myProfile) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/library/documents/${myProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
        if (data.length > 0 && !selectedDoc && activeTab === 'myDocuments') {
          setSelectedDoc(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchSharedDocuments = async () => {
    if (!myProfile) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/library/shared?researcher_id=${myProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setSharedDocuments(data);
        if (data.length > 0 && !selectedDoc && activeTab === 'sharedWithMe') {
          setSelectedDoc(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching shared documents:', error);
    }
  };

  const fetchFolders = async () => {
    if (!myProfile) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/folders/${myProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setFolders(data);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const useAICredits = async (action, documentName = null) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-credits/use`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          researcher_id: myProfile.id,
          action: action,
          document_name: documentName
        })
      });

      if (response.ok) {
        // Refresh credits display
        setCreditsRefreshTrigger(prev => prev + 1);
        return true;
      } else if (response.status === 402) {
        // Insufficient credits
        const errorData = await response.json();
        toast({
          title: "Insufficient AI Credits",
          description: `You need ${errorData.detail.credits_needed} credits but only have ${errorData.detail.credits_remaining}. Please purchase more credits or upgrade your plan.`,
          variant: "destructive"
        });
        return false;
      } else {
        toast({
          title: "Error",
          description: "Failed to process AI request",
          variant: "destructive"
        });
        return false;
      }
    } catch (error) {
      console.error('Error using AI credits:', error);
      toast({
        title: "Error",
        description: "Failed to process AI request",
        variant: "destructive"
      });
      return false;
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/folders/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newFolderName,
          researcher_id: myProfile.id,
          parent_folder_id: parentFolderId
        })
      });

      if (response.ok) {
        toast({
          title: t('folderCreated'),
          description: t('folderCreatedSuccess')
        });
        setNewFolderName('');
        setParentFolderId(null);
        setFolderDialogOpen(false);
        fetchFolders();
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      toast({
        title: t('error'),
        description: 'Failed to create folder',
        variant: "destructive"
      });
    }
  };

  const handleRenameFolder = async (folderId, newName) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/folders/${folderId}?researcher_id=${myProfile.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName })
      });

      if (response.ok) {
        toast({
          title: t('folderRenamed'),
          description: t('folderRenamedSuccess')
        });
        setRenamingFolderId(null);
        setRenameFolderValue('');
        fetchFolders();
      }
    } catch (error) {
      console.error('Error renaming folder:', error);
      toast({
        title: t('error'),
        description: 'Failed to rename folder',
        variant: "destructive"
      });
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!confirm(t('confirmDeleteFolder'))) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/folders/${folderId}?researcher_id=${myProfile.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: t('folderDeleted'),
          description: t('folderDeletedSuccess')
        });
        if (selectedFolder === folderId) {
          setSelectedFolder(null);
        }
        fetchFolders();
        fetchDocuments();
      } else {
        const errorData = await response.json();
        toast({
          title: t('error'),
          description: errorData.detail || 'Failed to delete folder',
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast({
        title: t('error'),
        description: 'Failed to delete folder',
        variant: "destructive"
      });
    }
  };

  const handleMoveDocument = async (documentId, folderId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/folders/move-document?researcher_id=${myProfile.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_id: documentId,
          folder_id: folderId
        })
      });

      if (response.ok) {
        toast({
          title: t('documentMoved'),
          description: t('documentMovedSuccess')
        });
        setMovingDocId(null);
        setMoveToFolderId(null);
        fetchDocuments();
        fetchFolders();
      }
    } catch (error) {
      console.error('Error moving document:', error);
      toast({
        title: t('error'),
        description: 'Failed to move document',
        variant: "destructive"
      });
    }
  };

  const toggleFolderExpand = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  // Build folder tree structure
  const buildFolderTree = (folders, parentId = null) => {
    return folders
      .filter(folder => folder.parent_folder_id === parentId)
      .map(folder => ({
        ...folder,
        children: buildFolderTree(folders, folder.id)
      }));
  };


  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !file.name.endsWith('.pdf')) {
      toast({
        title: t('invalidFile'),
        description: t('pleaseSelectPDF'),
        variant: "destructive"
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('researcher_id', myProfile.id);
    formData.append('researcher_name', myProfile.name);
    if (selectedFolder) {
      formData.append('folder_id', selectedFolder);
    }

    setUploading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/library/upload`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const newDoc = await response.json();
        toast({
          title: t('uploadSuccessful'),
          description: t('pdfProcessed')
        });
        fetchDocuments();
        setSelectedDoc(newDoc);
      } else {
        const error = await response.json();
        toast({
          title: t('uploadFailed'),
          description: error.detail || t('failedToUploadPDF'),
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: t('uploadFailed'),
        description: t('networkError'),
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  const handleSummarize = async () => {
    if (!selectedDoc) return;
    
    // Check and use AI credits (summary costs 2 credits)
    const hasCredits = await useAICredits('summary', selectedDoc.filename);
    if (!hasCredits) return;
    
    setSummarizing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/library/summarize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_id: selectedDoc.id,
          researcher_id: selectedDoc.researcher_id || myProfile.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedDoc({...selectedDoc, summary: data.summary, keywords: data.keywords || []});
        const updatedDocs = (activeTab === 'myDocuments' ? documents : sharedDocuments).map(doc => 
          doc.id === selectedDoc.id ? {...doc, summary: data.summary, keywords: data.keywords || []} : doc
        );
        if (activeTab === 'myDocuments') {
          setDocuments(updatedDocs);
        } else {
          setSharedDocuments(updatedDocs);
        }
        
        toast({
          title: t('summaryGenerated'),
          description: t('aiSummaryExtracted')
        });
      }
    } catch (error) {
      toast({
        title: t('summarizationFailed'),
        description: t('pleaseTryAgain'),
        variant: "destructive"
      });
    } finally {
      setSummarizing(false);
    }
  };

  const handleSendChat = async (question = null) => {
    const messageToSend = question || chatInput.trim();
    if (!messageToSend || !selectedDoc || chatLoading) return;

    // Check if the question is "Find Related Papers"
    if (messageToSend === t('findRelatedPapers') || messageToSend === 'Find Related Papers') {
      handleFindRelatedPapers();
      return;
    }

    // Check and use AI credits (chat costs 1 credit)
    const hasCredits = await useAICredits('chat', selectedDoc.filename);
    if (!hasCredits) {
      // Remove the user message we just added since request failed
      return;
    }

    if (!question) setChatInput('');
    
    setChatMessages(prev => [...prev, { role: 'user', content: messageToSend }]);
    setChatLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/library/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_id: selectedDoc.id,
          researcher_id: selectedDoc.researcher_id || myProfile.id,
          message: messageToSend,
          session_id: chatSessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (!chatSessionId) setChatSessionId(data.session_id);
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      }
    } catch (error) {
      toast({
        title: t('chatFailed'),
        description: t('networkError'),
        variant: "destructive"
      });
    } finally {
      setChatLoading(false);
    }
  };

  const handleFindRelatedPapers = async () => {
    if (!selectedDoc) return;
    
    // Check and use AI credits (related papers costs 5 credits)
    const hasCredits = await useAICredits('related_papers', selectedDoc.filename);
    if (!hasCredits) return;
    
    setRelatedPapersLoading(true);
    setRelatedPapersDialogOpen(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/library/related-papers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_id: selectedDoc.id,
          researcher_id: selectedDoc.researcher_id || myProfile.id,
          query: selectedDoc.title,
          limit: 10
        })
      });

      if (response.ok) {
        const data = await response.json();
        setRelatedPapers(data.papers || []);
        toast({
          title: t('relatedPapersFound'),
          description: `Found ${data.total} related papers`
        });
      } else {
        const error = await response.json();
        toast({
          title: t('error'),
          description: error.detail || 'Failed to find related papers',
          variant: "destructive"
        });
        setRelatedPapersDialogOpen(false);
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: t('networkError'),
        variant: "destructive"
      });
      setRelatedPapersDialogOpen(false);
    } finally {
      setRelatedPapersLoading(false);
    }
  };

  const handleGenerateCitation = async () => {
    if (!selectedDoc) return;
    
    // Check and use AI credits (citation costs 1 credit)
    const hasCredits = await useAICredits('citation', selectedDoc.filename);
    if (!hasCredits) return;
    
    setCitationLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/library/citation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_id: selectedDoc.id,
          researcher_id: selectedDoc.researcher_id || myProfile.id,
          style: citationStyle
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedCitation(data.citation);
        toast({
          title: t('citationGenerated'),
          description: `${data.style} citation from Crossref`
        });
      }
    } catch (error) {
      toast({
        title: t('citationFailed'),
        description: t('pleaseTryAgain'),
        variant: "destructive"
      });
    } finally {
      setCitationLoading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/library/document/${docId}?researcher_id=${myProfile.id}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        toast({ title: t('documentDeleted') });
        const newDocs = documents.filter(d => d.id !== docId);
        setDocuments(newDocs);
        if (selectedDoc?.id === docId) {
          setSelectedDoc(newDocs[0] || null);
        }
      }
    } catch (error) {
      toast({
        title: t('deleteFailed'),
        variant: "destructive"
      });
    }
  };
  
  const handleShare = async () => {
    if (!selectedDoc) return;
    
    setSharingLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/library/share?researcher_id=${myProfile.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            document_id: selectedDoc.id,
            sharing_status: sharingStatus,
            shared_with: sharingStatus === 'shared' ? selectedResearchers : []
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast({
          title: t('sharingUpdated'),
          description: sharingStatus === 'shared' 
            ? `Document shared with ${selectedResearchers.length} researcher(s)`
            : `Document is now ${sharingStatus}`
        });
        setShareDialogOpen(false);
        // Update local document
        const updatedDocs = documents.map(doc => 
          doc.id === selectedDoc.id 
            ? { ...doc, sharing_status: sharingStatus, shared_with: sharingStatus === 'shared' ? selectedResearchers : [] }
            : doc
        );
        setDocuments(updatedDocs);
        setSelectedDoc({ ...selectedDoc, sharing_status: sharingStatus, shared_with: sharingStatus === 'shared' ? selectedResearchers : [] });
      } else {
        const error = await response.json();
        toast({
          title: t('sharingFailed'),
          description: error.detail || t('couldNotUpdateSharing'),
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: t('errorTitle'),
        description: t('failedToUpdateSharing'),
        variant: "destructive"
      });
    } finally {
      setSharingLoading(false);
    }
  };
  
  const fetchAllResearchers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/researchers/`);
      if (response.ok) {
        const data = await response.json();
        // Filter out current user
        const others = data.filter(r => r.id !== myProfile?.id);
        setAllResearchers(others);
      }
    } catch (error) {
      console.error('Error fetching researchers:', error);
    }
  };
  
  const openShareDialog = () => {
    if (selectedDoc) {
      setSharingStatus(selectedDoc.sharing_status || 'private');
      setSelectedResearchers(selectedDoc.shared_with || []);
      fetchAllResearchers(); // Load researchers when opening dialog
      setShareDialogOpen(true);
    }
  };
  
  const toggleResearcher = (researcherId) => {
    setSelectedResearchers(prev => {
      if (prev.includes(researcherId)) {
        return prev.filter(id => id !== researcherId);
      } else {
        return [...prev, researcherId];
      }
    });
  };

  const handleRename = async (docId, newTitle) => {
    if (!newTitle.trim()) {
      toast({
        title: t('invalidTitle'),
        description: t('titleCannotBeEmpty'),
        variant: "destructive"
      });
      return;
    }

    try {
      // Update document title via backend
      const response = await fetch(
        `${BACKEND_URL}/api/library/document/${docId}?researcher_id=${myProfile.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: newTitle.trim() })
        }
      );

      if (response.ok) {
        // Update local state
        setDocuments(docs => docs.map(d => 
          d.id === docId ? { ...d, title: newTitle.trim() } : d
        ));
        if (selectedDoc?.id === docId) {
          setSelectedDoc({ ...selectedDoc, title: newTitle.trim() });
        }
        toast({ title: t('documentRenamed') });
        setRenamingDocId(null);
      } else {
        toast({
          title: t('renameFailed'),
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: t('renameFailed'),
        variant: "destructive"
      });
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = (
      doc.title.toLowerCase().includes(query) ||
      (doc.authors && doc.authors.toLowerCase().includes(query))
    );
    
    // Filter by selected folder
    const matchesFolder = selectedFolder === null 
      ? true  // Show ALL documents when "All Documents" is selected
      : doc.folder_id === selectedFolder;  // Show documents in selected folder
    
    return matchesSearch && matchesFolder;
  });

  const filteredSharedDocuments = sharedDocuments.filter(doc => {
    const query = searchQuery.toLowerCase();
    return (
      doc.title.toLowerCase().includes(query) ||
      (doc.authors && doc.authors.toLowerCase().includes(query))
    );
  });

  const selectNextDoc = () => {
    const idx = documents.findIndex(d => d.id === selectedDoc?.id);
    if (idx < documents.length - 1) {
      setSelectedDoc(documents[idx + 1]);
    }
  };

  const selectPrevDoc = () => {
    const idx = documents.findIndex(d => d.id === selectedDoc?.id);
    if (idx > 0) {
      setSelectedDoc(documents[idx - 1]);
    }
  };

  // Show authentication required state
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-cyan-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('authenticationRequired')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('signInToAccessLibrary')}
                </p>
              </div>
              <div className="flex gap-3 justify-center">
                <Button 
                  onClick={() => navigate('/')}
                  className="bg-cyan-500 hover:bg-cyan-600"
                >
                  {t('signIn')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && !myProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-12 w-12 animate-spin text-cyan-600" />
      </div>
    );
  }

  // Show error state with retry option
  if (!loading && profileError && !myProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <X className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {profileError === 'profile_not_found' ? t('profileNotFound') : t('errorLoadingProfile')}
                </h3>
                <p className="text-sm text-gray-600">
                  {profileError === 'profile_not_found' 
                    ? t('needProfileForLibrary')
                    : t('unableToLoadProfile')}
                </p>
              </div>
              <div className="flex gap-3 justify-center">
                <Button 
                  onClick={fetchResearcherProfile}
                  className="bg-cyan-500 hover:bg-cyan-600"
                >
                  {t('retry')}
                </Button>
                {profileError === 'profile_not_found' && (
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                  >
                    {t('createProfile')}
                  </Button>
                )}
                <Button 
                  variant="outline"
                  onClick={() => navigate('/')}
                >
                  {t('goHome')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }


  // Count documents in folder and all subfolders recursively
  const countDocumentsInFolder = (folderId, includeSubfolders = true) => {
    let count = documents.filter(d => d.folder_id === folderId).length;
    
    if (includeSubfolders) {
      // Find all subfolders and count their documents
      const getSubfolderIds = (parentId) => {
        const subfolders = folders.filter(f => f.parent_folder_id === parentId);
        let ids = subfolders.map(f => f.id);
        subfolders.forEach(f => {
          ids = [...ids, ...getSubfolderIds(f.id)];
        });
        return ids;
      };
      
      const allSubfolderIds = getSubfolderIds(folderId);
      allSubfolderIds.forEach(subfolderId => {
        count += documents.filter(d => d.folder_id === subfolderId).length;
      });
    }
    
    return count;
  };

  // Folder Tree Item Component
  const FolderTreeItem = ({ folder, level, selectedFolder, expandedFolders, onSelect, onToggle, onRename, onDelete, onCreateSubfolder, documents, t }) => {
    const isExpanded = expandedFolders.has(folder.id);
    const hasChildren = folder.children && folder.children.length > 0;
    const documentCount = countDocumentsInFolder(folder.id, false); // Only direct documents, not subfolders
    const isRenaming = renamingFolderId === folder.id;

    return (
      <div className="select-none">
        <div
          className={`flex items-center gap-1 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-100 ${
            selectedFolder === folder.id ? 'bg-cyan-50 text-cyan-700' : 'text-gray-700'
          }`}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
        >
          {/* Expand/Collapse Icon */}
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle(folder.id);
              }}
              className="p-0.5 hover:bg-gray-200 rounded"
            >
              <ChevronRight className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
            </button>
          )}
          {!hasChildren && <div className="w-4" />}

          {/* Folder Icon */}
          <FolderOpen className="h-4 w-4 flex-shrink-0" />

          {/* Folder Name - Editable */}
          {isRenaming ? (
            <input
              type="text"
              value={renameFolderValue}
              onChange={(e) => setRenameFolderValue(e.target.value)}
              onBlur={() => {
                if (renameFolderValue.trim() && renameFolderValue !== folder.name) {
                  handleRenameFolder(folder.id, renameFolderValue);
                } else {
                  setRenamingFolderId(null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (renameFolderValue.trim() && renameFolderValue !== folder.name) {
                    handleRenameFolder(folder.id, renameFolderValue);
                  } else {
                    setRenamingFolderId(null);
                  }
                } else if (e.key === 'Escape') {
                  setRenamingFolderId(null);
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 px-1 py-0.5 text-sm border border-cyan-500 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500"
              autoFocus
            />
          ) : (
            <span
              onClick={() => onSelect(folder.id)}
              className="flex-1 text-sm font-medium truncate"
            >
              {folder.name}
            </span>
          )}

          {/* Document Count */}
          <span className="text-xs text-gray-500">({documentCount})</span>

          {/* Actions Menu */}
          <div className="relative group">
            <button
              onClick={(e) => e.stopPropagation()}
              className="p-0.5 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100"
            >
              <MoreVertical className="h-3 w-3" />
            </button>
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 hidden group-hover:block min-w-[140px]">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateSubfolder(folder.id);
                }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 flex items-center gap-2"
              >
                <FolderOpen className="h-3 w-3" />
                {t('createFolder')}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRename(folder.id);
                }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 flex items-center gap-2"
              >
                <Edit2 className="h-3 w-3" />
                {t('rename')}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSharingFolderId(folder.id);
                  fetchAllResearchers(); // Load researchers when opening dialog
                  setShareFolderDialogOpen(true);
                }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 flex items-center gap-2"
              >
                <Share2 className="h-3 w-3" />
                {t('shareFolder')}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(folder.id);
                }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 flex items-center gap-2 text-red-600"
              >
                <Trash2 className="h-3 w-3" />
                {t('delete')}
              </button>
            </div>
          </div>
        </div>

        {/* Children Folders */}
        {isExpanded && hasChildren && (
          <div>
            {folder.children.map(child => (
              <FolderTreeItem
                key={child.id}
                folder={child}
                level={level + 1}
                selectedFolder={selectedFolder}
                expandedFolders={expandedFolders}
                onSelect={onSelect}
                onToggle={onToggle}
                onRename={onRename}
                onDelete={onDelete}
                onCreateSubfolder={onCreateSubfolder}
                documents={documents}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
    );
  };


  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_knowledgelink-1/artifacts/8zfj25zj_IMG_6196.png" 
            alt="Logo" 
            className="h-8 w-8"
          />
          <span className="text-xl font-bold text-gray-900">{t('researcherLibrary')}</span>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
            <Home className="h-4 w-4 mr-2" />
            {t('home')}
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Navigation & File List */}
        <div className="w-72 min-w-[288px] max-w-[288px] bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
          {/* Tabs Section */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col gap-2">
              <Button
                variant={activeTab === 'myDocuments' ? 'default' : 'outline'}
                className={`w-full justify-start ${activeTab === 'myDocuments' ? 'bg-cyan-500 hover:bg-cyan-600' : ''}`}
                onClick={() => {
                  setActiveTab('myDocuments');
                  if (documents.length > 0) {
                    setSelectedDoc(documents[0]);
                  }
                }}
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                {t('myDocuments')}
              </Button>
              <Button
                variant={activeTab === 'sharedWithMe' ? 'default' : 'outline'}
                className={`w-full justify-start ${activeTab === 'sharedWithMe' ? 'bg-cyan-500 hover:bg-cyan-600' : ''}`}
                onClick={() => {
                  setActiveTab('sharedWithMe');
                  if (sharedDocuments.length > 0) {
                    setSelectedDoc(sharedDocuments[0]);
                  }
                }}
              >
                <Users className="h-4 w-4 mr-2" />
                {t('sharedWithMe')}
              </Button>
            </div>
          </div>

          {/* Upload Section - Only show for My Documents */}
          {activeTab === 'myDocuments' && (
            <div className="p-4 border-b border-gray-200">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                id="pdf-upload-input"
                className="hidden"
                disabled={uploading}
              />
              <label htmlFor="pdf-upload-input">
                <Button 
                  className="w-full bg-cyan-500 hover:bg-cyan-600" 
                  disabled={uploading}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('pdf-upload-input').click();
                  }}
                  type="button"
                >
                  {uploading ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      {t('uploadPDF')}
                    </>
                  )}
                </Button>
              </label>
            </div>
          )}

          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder={t('searchPapers')}
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Folders Section - Only show for My Documents */}
          {activeTab === 'myDocuments' && folders.length > 0 && (
            <div className="border-b border-gray-200">
              <div className="p-4 pb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase">{t('folders')}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2"
                    onClick={() => {
                      setParentFolderId(null);
                      setFolderDialogOpen(true);
                    }}
                  >
                    <span className="text-xs">+ {t('newFolder')}</span>
                  </Button>
                </div>
              </div>
              
              {/* Folder Tree */}
              <div className="overflow-y-auto px-2 pb-2" style={{ maxHeight: '250px' }}>
                {/* Root Level - All Documents */}
                <div
                  onClick={() => setSelectedFolder(null)}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-100 ${
                    selectedFolder === null ? 'bg-cyan-50 text-cyan-700' : 'text-gray-700'
                  }`}
                >
                  <Home className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium truncate">All Documents</span>
                  <span className="ml-auto text-xs text-gray-500">
                    ({documents.length})
                  </span>
                </div>

                {/* Folder Tree */}
                {buildFolderTree(folders).map(folder => (
                  <FolderTreeItem
                    key={folder.id}
                    folder={folder}
                    level={0}
                    selectedFolder={selectedFolder}
                    expandedFolders={expandedFolders}
                    onSelect={setSelectedFolder}
                    onToggle={toggleFolderExpand}
                    onRename={(id) => {
                      setRenamingFolderId(id);
                      setRenameFolderValue(folder.name);
                    }}
                    onDelete={handleDeleteFolder}
                    onCreateSubfolder={(id) => {
                      setParentFolderId(id);
                      setFolderDialogOpen(true);
                    }}
                    documents={documents}
                    t={t}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Create Folder Button - Show when no folders exist */}
          {activeTab === 'myDocuments' && folders.length === 0 && (
            <div className="p-4 border-b border-gray-200">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setParentFolderId(null);
                  setFolderDialogOpen(true);
                }}
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                {t('createFolder')}
              </Button>
            </div>
          )}

          {/* File List */}
          <div className="overflow-y-auto" style={{ maxHeight: '250px' }}>
            <div className="p-2">
              <div className="text-xs font-semibold text-gray-500 px-2 py-1">
                {activeTab === 'myDocuments' 
                  ? `${t('files')} (${filteredDocuments.length})`
                  : `${t('sharedDocuments')} (${filteredSharedDocuments.length})`}
              </div>
              {(activeTab === 'myDocuments' ? filteredDocuments : filteredSharedDocuments).map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => {
                    try {
                      setSelectedDoc(doc);
                    } catch (error) {
                      console.error('Error selecting document:', error);
                      toast({
                        title: t('errorTitle'),
                        description: t('failedToOpenDocument'),
                        variant: "destructive"
                      });
                    }
                  }}
                  className={`p-3 mb-1 rounded-lg cursor-pointer transition-colors ${
                    selectedDoc?.id === doc.id
                      ? 'bg-cyan-50 border-l-4 border-cyan-500'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <FileText className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                      selectedDoc?.id === doc.id ? 'text-cyan-600' : 'text-gray-400'
                    }`} />
                    <div className="flex-1 min-w-0">
                      {renamingDocId === doc.id ? (
                        <div className="space-y-1" onClick={(e) => e.stopPropagation()}>
                          <Input
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleRename(doc.id, renameValue);
                              } else if (e.key === 'Escape') {
                                setRenamingDocId(null);
                              }
                            }}
                            className="text-sm h-7 px-2 w-full"
                            autoFocus
                          />
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-xs flex-1"
                              onClick={() => handleRename(doc.id, renameValue)}
                            >
                              <Check className="h-3 w-3 mr-1 text-green-600" />
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-xs flex-1"
                              onClick={() => setRenamingDocId(null)}
                            >
                              <X className="h-3 w-3 mr-1 text-red-600" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="group flex items-start justify-between gap-1">
                          <div className="font-medium text-sm text-gray-900 line-clamp-2 flex-1 min-w-0">
                            {doc.title}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-5 w-5 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRenamingDocId(doc.id);
                              setRenameValue(doc.title);
                            }}
                          >
                            <Edit2 className="h-3 w-3 text-gray-500" />
                          </Button>
                        </div>
                      )}
                      {doc.authors && (
                        <div className="text-xs text-gray-500 mt-1 line-clamp-1">
                          {doc.authors}
                        </div>
                      )}
                      {doc.year && (
                        <div className="text-xs text-gray-400 mt-1">
                          {doc.year}
                        </div>
                      )}
                      {doc.summary && (
                        <Badge className="mt-2 bg-green-100 text-green-700 text-xs">
                          <Sparkles className="h-3 w-3 mr-1" />
                          Summarized
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {(activeTab === 'myDocuments' ? filteredDocuments : filteredSharedDocuments).length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <BookOpen className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">
                    {activeTab === 'myDocuments' 
                      ? t('noDocumentsFound')
                      : t('noSharedDocumentsFound')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Section - Storage + PDF Viewer + Chat */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Storage Indicator - Horizontal */}
          <div className="bg-white border-b border-gray-200 px-6 py-3 shadow-sm">
            <StorageIndicatorHorizontal refreshTrigger={documents.length} />
          </div>

          {/* AI Credits Indicator */}
          <div className="bg-white border-b border-gray-200 px-6 py-3">
            <AICreditsIndicator 
              researcherId={myProfile?.id} 
              refreshTrigger={creditsRefreshTrigger} 
            />
          </div>

          {/* Content Row - PDF Viewer + Chat */}
          <div className="flex-1 flex overflow-hidden">
            {/* Middle - PDF Viewer */}
            <div className="flex-1 min-w-0 flex flex-col bg-white overflow-hidden">
          {selectedDoc ? (
            <>
              {/* PDF Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 truncate">
                      {selectedDoc.title}
                    </h2>
                    {selectedDoc.authors && (
                      <p className="text-sm text-gray-600 mt-1">{selectedDoc.authors}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Only show folder/share/delete buttons for own documents */}
                    {activeTab === 'myDocuments' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setMovingDocId(selectedDoc.id);
                            setMoveToFolderId(selectedDoc.folder_id || null);
                          }}
                          title={t('moveToFolder')}
                        >
                          <FolderOpen className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={openShareDialog}
                          title="Share document"
                        >
                          <Share2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const url = `${BACKEND_URL}/api/library/download/${selectedDoc.id}?researcher_id=${selectedDoc.researcher_id || myProfile.id}`;
                        window.open(url, '_blank');
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    {/* Only show delete button for own documents */}
                    {activeTab === 'myDocuments' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(selectedDoc.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* PDF Viewer */}
              <div className="flex-1 overflow-hidden">
                <iframe
                  src={`${BACKEND_URL}/api/library/view/${selectedDoc.id}?researcher_id=${selectedDoc.researcher_id || myProfile.id}#navpanes=0&view=FitH`}
                  className="w-full h-full border-0"
                  title="PDF Viewer"
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <BookOpen className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>{t('selectDocumentToView')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar - TL;DR / Content / Chat / Citation */}
        <div className="w-96 min-w-[384px] max-w-[384px] bg-white border-l border-gray-200 flex flex-col flex-shrink-0">
          {selectedDoc && (
            <>
              {/* Navigation */}
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <div className="flex gap-1">
                  <Button
                    variant={currentView === 'tldr' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setCurrentView('tldr')}
                    className={currentView === 'tldr' ? 'bg-cyan-500' : ''}
                  >
                    {t('tldr')}
                  </Button>
                  <Button
                    variant={currentView === 'chat' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setCurrentView('chat')}
                    className={currentView === 'chat' ? 'bg-cyan-500' : ''}
                  >
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={currentView === 'citation' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setCurrentView('citation')}
                    className={currentView === 'citation' ? 'bg-cyan-500' : ''}
                  >
                    <Quote className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={selectPrevDoc}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={selectNextDoc}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* TL;DR View */}
                {currentView === 'tldr' && (
                  <div className="space-y-4">
                    {selectedDoc.summary ? (
                      <>
                        <div className="prose prose-sm max-w-none">
                          <p className="text-gray-800 whitespace-pre-wrap">{selectedDoc.summary}</p>
                        </div>
                        {selectedDoc.keywords && selectedDoc.keywords.length > 0 && (
                          <div className="pt-4 border-t">
                            <div className="text-sm font-semibold text-gray-700 mb-2">Keywords</div>
                            <div className="flex flex-wrap gap-2">
                              {selectedDoc.keywords.map((kw, idx) => (
                                <Badge key={idx} variant="outline" className="bg-cyan-50">
                                  {kw}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <Sparkles className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p className="text-gray-600 mb-4">{t('generateAISummary')}</p>
                        <Button
                          onClick={handleSummarize}
                          disabled={summarizing}
                          className="bg-cyan-500 hover:bg-cyan-600"
                        >
                          {summarizing ? (
                            <>
                              <Loader className="h-4 w-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              {t('generateSummary')}
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Chat View */}
                {currentView === 'chat' && (
                  <div className="h-full flex flex-col space-y-3">
                    {/* Predefined Questions - Dropdown */}
                    <div className="border-b border-gray-200 pb-3">
                      <div className="text-xs font-semibold text-gray-700 mb-2">{t('quickQuestions')}</div>
                      <select
                        key={selectKey}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleSendChat(e.target.value);
                            setSelectKey(prev => prev + 1);
                          }
                        }}
                        disabled={chatLoading}
                        className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        defaultValue=""
                      >
                        <option value="" disabled>Select a question to ask...</option>
                        {getPredefinedQuestions().map((q, idx) => (
                          <option key={`question-${idx}`} value={q}>
                            {q}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Chat Messages */}
                    <div className="flex-1 overflow-y-auto space-y-3">
                      {chatMessages.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <MessageSquare className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">{t('selectQuestionOrType')}</p>
                        </div>
                      ) : (
                        <>
                          {chatMessages.map((msg, idx) => (
                            <div
                              key={idx}
                              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[85%] p-3 rounded-lg text-sm ${
                                  msg.role === 'user'
                                    ? 'bg-cyan-500 text-white'
                                    : 'bg-gray-100 text-gray-900'
                                }`}
                              >
                                {msg.role === 'assistant' ? (
                                  <div className="prose prose-sm max-w-none">
                                    <ReactMarkdown
                                      components={{
                                        a: ({ node, ...props }) => (
                                          <a
                                            {...props}
                                            className="text-cyan-600 hover:text-cyan-700 underline font-medium"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          />
                                        ),
                                        h3: ({ node, ...props }) => (
                                          <h3 {...props} className="text-base font-bold mt-3 mb-2 text-gray-900" />
                                        ),
                                        p: ({ node, ...props }) => (
                                          <p {...props} className="mb-2 text-gray-800" />
                                        ),
                                        ul: ({ node, ...props }) => (
                                          <ul {...props} className="list-none pl-0 mb-2" />
                                        ),
                                        li: ({ node, ...props }) => (
                                          <li {...props} className="mb-1" />
                                        ),
                                        hr: ({ node, ...props }) => (
                                          <hr {...props} className="my-3 border-gray-300" />
                                        ),
                                        em: ({ node, ...props }) => (
                                          <em {...props} className="text-gray-600 italic" />
                                        ),
                                        strong: ({ node, ...props }) => (
                                          <strong {...props} className="font-bold text-gray-900" />
                                        )
                                      }}
                                    >
                                      {msg.content}
                                    </ReactMarkdown>
                                  </div>
                                ) : (
                                  msg.content
                                )}
                              </div>
                            </div>
                          ))}
                          {chatLoading && (
                            <div className="flex justify-start">
                              <div className="bg-gray-100 p-2 rounded-lg">
                                <Loader className="h-4 w-4 animate-spin text-cyan-600" />
                              </div>
                            </div>
                          )}
                          <div ref={chatEndRef} />
                        </>
                      )}
                    </div>

                    {/* Chat Input */}
                    <div className="pt-3 border-t border-gray-200">
                      <div className="flex gap-2">
                        <Textarea
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendChat();
                            }
                          }}
                          placeholder={t('askAboutPaper')}
                          className="resize-none text-sm"
                          rows={2}
                          disabled={chatLoading}
                        />
                        <Button
                          onClick={() => handleSendChat()}
                          disabled={!chatInput.trim() || chatLoading}
                          size="sm"
                          className="bg-cyan-500 hover:bg-cyan-600"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Citation View */}
                {currentView === 'citation' && (
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-2">{t('citationStyle')}</div>
                      <div className="flex gap-2">
                        {['APA', 'MLA', 'Chicago', 'Harvard'].map(style => (
                          <Button
                            key={style}
                            variant={citationStyle === style ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCitationStyle(style)}
                            className={citationStyle === style ? 'bg-cyan-500' : ''}
                          >
                            {style}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <Button
                      onClick={handleGenerateCitation}
                      disabled={citationLoading}
                      className="w-full bg-cyan-500 hover:bg-cyan-600"
                    >
                      {citationLoading ? (
                        <>
                          <Loader className="h-4 w-4 mr-2 animate-spin" />
                          {t('generating')}
                        </>
                      ) : (
                        <>
                          <Quote className="h-4 w-4 mr-2" />
                          {t('generateFromCrossref')}
                        </>
                      )}
                    </Button>

                    {generatedCitation && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-700">{citationStyle} Citation</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              navigator.clipboard.writeText(generatedCitation);
                              toast({ title: t('copied') });
                            }}
                          >
                            {t('copyCitation')}
                          </Button>
                        </div>
                        <p className="text-sm text-gray-800">{generatedCitation}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          Generated via Crossref API
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
          </div>
        </div>
      </div>
      

      {/* Create Folder Dialog */}
      <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('createNewFolder')}</DialogTitle>
            <DialogDescription>
              {parentFolderId ? t('createSubfolderDesc') : t('createRootFolderDesc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {t('folderName')}
              </label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder={t('enterFolderName')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newFolderName.trim()) {
                    handleCreateFolder();
                  }
                }}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setFolderDialogOpen(false);
              setNewFolderName('');
              setParentFolderId(null);
            }}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim()}
              className="bg-cyan-500 hover:bg-cyan-600"
            >
              {t('createFolder')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Document Dialog */}
      <Dialog open={movingDocId !== null} onOpenChange={(open) => {
        if (!open) {
          setMovingDocId(null);
          setMoveToFolderId(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('moveToFolder')}</DialogTitle>
            <DialogDescription>
              Select a folder to move the document to
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 py-4 max-h-96 overflow-y-auto">
            {/* Root Option */}
            <Button
              variant={moveToFolderId === null ? 'default' : 'outline'}
              className="w-full justify-start"
              onClick={() => setMoveToFolderId(null)}
            >
              <Home className="h-4 w-4 mr-2" />
              {t('rootFolder')}
            </Button>
            
            {/* Folder Options */}
            {folders.map(folder => (
              <Button
                key={folder.id}
                variant={moveToFolderId === folder.id ? 'default' : 'outline'}
                className="w-full justify-start"
                onClick={() => setMoveToFolderId(folder.id)}
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                {folder.name}
                {folder.parent_folder_id && (
                  <span className="ml-auto text-xs text-gray-500">
                    (subfolder)
                  </span>
                )}
              </Button>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setMovingDocId(null);
              setMoveToFolderId(null);
            }}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={() => {
                if (movingDocId) {
                  handleMoveDocument(movingDocId, moveToFolderId);
                }
              }}
              className="bg-cyan-500 hover:bg-cyan-600"
            >
              {t('moveTo')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Folder Dialog */}
      <Dialog open={shareFolderDialogOpen} onOpenChange={setShareFolderDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('shareFolder')}</DialogTitle>
            <DialogDescription>
              Manage folder sharing and permissions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              {/* Private Option */}
              <Button
                variant={sharingStatus === 'private' ? 'default' : 'outline'}
                className="w-full justify-start"
                onClick={() => setSharingStatus('private')}
              >
                <Lock className="h-4 w-4 mr-2" />
                <div className="text-left">
                  <div className="font-medium">Private</div>
                  <div className="text-xs text-gray-500">Only you can access</div>
                </div>
              </Button>
              
              {/* Shared Option */}
              <div className="border rounded-lg">
                <Button
                  variant={sharingStatus === 'shared' ? 'default' : 'outline'}
                  className="w-full justify-start rounded-b-none"
                  onClick={() => setSharingStatus('shared')}
                >
                  <Users className="h-4 w-4 mr-2" />
                  <div className="text-left">
                    <div className="font-medium">Share with Researchers</div>
                    <div className="text-xs text-gray-500">
                      {selectedResearchers.length > 0 
                        ? `Selected: ${selectedResearchers.length} researcher(s)` 
                        : 'Choose who can access'}
                    </div>
                  </div>
                </Button>
                
                {sharingStatus === 'shared' && (
                  <div className="p-4 border-t bg-gray-50 max-h-64 overflow-y-auto">
                    <div className="text-sm font-medium text-gray-700 mb-3">
                      {t('selectResearchers')}:
                    </div>
                    {allResearchers.length === 0 ? (
                      <p className="text-sm text-gray-500">Loading researchers...</p>
                    ) : (
                      <div className="space-y-2">
                        {allResearchers.map((researcher) => (
                          <div
                            key={researcher.id}
                            className="flex items-center space-x-3 p-2 hover:bg-white rounded-lg cursor-pointer"
                            onClick={() => toggleResearcher(researcher.id)}
                          >
                            <Checkbox
                              checked={selectedResearchers.includes(researcher.id)}
                              onCheckedChange={() => toggleResearcher(researcher.id)}
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {researcher.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {researcher.university || researcher.institution || 'Researcher'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareFolderDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={async () => {
                if (!sharingFolderId) return;
                
                try {
                  const response = await fetch(`${BACKEND_URL}/api/folders/share?researcher_id=${myProfile.id}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      folder_id: sharingFolderId,
                      sharing_status: sharingStatus,
                      shared_with: sharingStatus === 'shared' ? selectedResearchers : [],
                      permissions: sharingStatus === 'shared' 
                        ? selectedResearchers.reduce((acc, id) => ({ ...acc, [id]: 'view' }), {})
                        : {}
                    })
                  });

                  if (response.ok) {
                    toast({
                      title: t('folderShared'),
                      description: t('folderSharedSuccess')
                    });
                    setShareFolderDialogOpen(false);
                    setSharingFolderId(null);
                    setSelectedResearchers([]);
                    fetchFolders();
                  }
                } catch (error) {
                  console.error('Error sharing folder:', error);
                  toast({
                    title: t('error'),
                    description: 'Failed to share folder',
                    variant: "destructive"
                  });
                }
              }}
              disabled={sharingStatus === 'shared' && selectedResearchers.length === 0}
              className="bg-cyan-500 hover:bg-cyan-600"
            >
              {t('shareFolder')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('shareDocument')}</DialogTitle>
            <DialogDescription>
              Choose who can access "{selectedDoc?.title}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              {/* Private Option */}
              <Button
                variant={sharingStatus === 'private' ? 'default' : 'outline'}
                className="w-full justify-start"
                onClick={() => setSharingStatus('private')}
              >
                <Lock className="h-4 w-4 mr-2" />
                <div className="text-left">
                  <div className="font-medium">{t('privateDocument')}</div>
                  <div className="text-xs text-gray-500">{t('onlyYouCanView')}</div>
                </div>
              </Button>
              
              {/* Shared with Selected Researchers Option */}
              <div className="border rounded-lg">
                <Button
                  variant={sharingStatus === 'shared' ? 'default' : 'outline'}
                  className="w-full justify-start rounded-b-none"
                  onClick={() => setSharingStatus('shared')}
                >
                  <Users className="h-4 w-4 mr-2" />
                  <div className="text-left">
                    <div className="font-medium">Share with Specific Researchers</div>
                    <div className="text-xs text-gray-500">
                      {selectedResearchers.length > 0 
                        ? `Selected: ${selectedResearchers.length} researcher(s)` 
                        : 'Choose who can access'}
                    </div>
                  </div>
                </Button>
                
                {/* Researcher Selector - Only show when 'shared' is selected */}
                {sharingStatus === 'shared' && (
                  <div className="p-4 border-t bg-gray-50 max-h-64 overflow-y-auto">
                    <div className="text-sm font-medium text-gray-700 mb-3">
                      Select Researchers:
                    </div>
                    {allResearchers.length === 0 ? (
                      <p className="text-sm text-gray-500">Loading researchers...</p>
                    ) : (
                      <div className="space-y-2">
                        {allResearchers.map((researcher) => (
                          <div
                            key={researcher.id}
                            className="flex items-center space-x-3 p-2 hover:bg-white rounded-lg cursor-pointer"
                            onClick={() => toggleResearcher(researcher.id)}
                          >
                            <Checkbox
                              checked={selectedResearchers.includes(researcher.id)}
                              onCheckedChange={() => toggleResearcher(researcher.id)}
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {researcher.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {researcher.university || researcher.institution || 'Researcher'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Public Option */}
              <Button
                variant={sharingStatus === 'public' ? 'default' : 'outline'}
                className="w-full justify-start"
                onClick={() => setSharingStatus('public')}
              >
                <Globe className="h-4 w-4 mr-2" />
                <div className="text-left">
                  <div className="font-medium">{t('publicDocument')}</div>
                  <div className="text-xs text-gray-500">{t('anyoneWithLink')}</div>
                </div>
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleShare} 
              disabled={sharingLoading || (sharingStatus === 'shared' && selectedResearchers.length === 0)}
            >
              {sharingLoading ? 'Updating...' : 'Update Sharing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Related Papers Dialog */}
      <Dialog open={relatedPapersDialogOpen} onOpenChange={setRelatedPapersDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-cyan-600" />
              Related Papers
            </DialogTitle>
            <DialogDescription>
              Papers related to: <span className="font-semibold">{selectedDoc?.title}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {relatedPapersLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="h-8 w-8 animate-spin text-cyan-600" />
              </div>
            ) : relatedPapers.length > 0 ? (
              <>
                {relatedPapers.map((paper, index) => (
                  <Card key={index} className="border border-gray-200 hover:border-cyan-300 transition-colors">
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-gray-900 text-sm leading-snug">
                            {paper.title}
                          </h3>
                          {paper.citations !== null && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 flex-shrink-0">
                              {paper.citations} citations
                            </Badge>
                          )}
                        </div>
                        
                        {paper.authors && paper.authors.length > 0 && (
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <User className="h-3 w-3" />
                            <span>{paper.authors.slice(0, 3).join(', ')}{paper.authors.length > 3 ? ' et al.' : ''}</span>
                          </div>
                        )}
                        
                        {paper.year && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Calendar className="h-3 w-3" />
                            <span>{paper.year}</span>
                          </div>
                        )}
                        
                        {paper.abstract && (
                          <p className="text-xs text-gray-700 line-clamp-3">
                            {paper.abstract}
                          </p>
                        )}
                        
                        {paper.url && (
                          <div className="pt-2">
                            <a
                              href={paper.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-cyan-600 hover:text-cyan-700 hover:underline inline-flex items-center gap-1"
                            >
                              View on Semantic Scholar
                              <Globe className="h-3 w-3" />
                            </a>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Search className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No related papers found</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setRelatedPapersDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResearcherLibraryNew;
